import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

const mappedLabel = (label: string): string => (label === '&laquo; Previous' ?
  'Previous' :
  label === 'Next &raquo;' ?
    'Next' :
    label)

export interface IPaginationProps {
  totalItems: number
  itemsPerPage: number
}

interface IPaginationLink {
  label: string
  label_key: string
  page: number
  disabled?: boolean
}

const getPaginationLinks = (
  totalPages: number,
  currentPage: number,
): IPaginationLink[] => {
  const links: IPaginationLink[] = []
  const buttonsAmount = 4

  if (totalPages <= 1) {
    return links
  }

  const generateLink = (label: string, page: number, disabled?: boolean) => ({
    label,
    label_key: label,
    page,
    disabled,
  })

  const addLinksInRange = (start: number, end: number) => {
    for (let i = start; i <= end; i++) {
      links.push(generateLink(i.toString(), i))
    }
  }

  const addDots = (keyPrefix?: string) => {
    links.push({
      label: '...',
      label_key: keyPrefix ? `${keyPrefix}...` : '...',
      disabled: false,
      page: -1,
    })
  }

  const addPreviousLink = () => {
    links.push(
      generateLink(
        '&laquo; Previous',
        currentPage - 1,
        currentPage === 1 || totalPages <= 2,
      ),
    )
  }

  const addNextLink = () => {
    links.push(
      generateLink(
        'Next &raquo;',
        currentPage + 1,
        currentPage === totalPages || totalPages <= 2,
      ),
    )
  }

  addPreviousLink()
  if (totalPages <= buttonsAmount + 1) {
    addLinksInRange(1, totalPages)
  } else if (currentPage <= buttonsAmount) {
    addLinksInRange(1, buttonsAmount + 1)
    addDots()
    addLinksInRange(totalPages, totalPages)
  } else if (currentPage > totalPages - buttonsAmount) {
    addLinksInRange(1, 1)
    addDots()
    addLinksInRange(totalPages - buttonsAmount, totalPages)
  } else {
    addLinksInRange(1, 1)
    addDots()
    if (currentPage === totalPages) {
      addLinksInRange(currentPage - 2, currentPage)
    } else {
      addLinksInRange(currentPage - 1, currentPage + 1)
      addDots('1')
      addLinksInRange(totalPages, totalPages)
    }
  }
  addNextLink()
  return links
}

const getCurrentPage = (
  queryValue: string | null,
  pageIndex?: number,
): number => {
  if (pageIndex !== undefined) {
    return pageIndex + 1
  }
  const page = queryValue
  if (page && !isNaN(Number(page)) && page !== '0') {
    return Number(page)
  }
  return 1
}

export const Pagination: React.FC<IPaginationProps> = ({
  totalItems,
  itemsPerPage,
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const [searchParams, setSearchParams] = useSearchParams('')

  const [currentPage, setCurrentPage] = useState<number>(
    getCurrentPage(searchParams.get('p')),
  )

  useEffect(() => {
    setCurrentPage(getCurrentPage(searchParams.get('p')))
  }, [searchParams.get('p')])

  const handlePaginationChange = (page: number): void => {
    if (page === currentPage) return
    if (page === 1) {
      searchParams.delete('p')
    } else {
      searchParams.set('p', page.toString())
    }
    setSearchParams(searchParams)
    setCurrentPage(page)
  }

  return (
    <ul className="flex">
      {getPaginationLinks(totalPages, currentPage)
        .map(link => ({
          ...link,
          label: mappedLabel(link.label),
          label_key: mappedLabel(link.label_key),
        }))
        .map(link => (
          <li
            key={link.label_key}
            className={clsx('!rounded-md !border-none', {
              'tc-mv-blur': currentPage === link.page,
              'hidden': link.disabled,
            })}
          >
            <button
              className="flex h-10 justify-center items-center min-w-10"
              onClick={() => handlePaginationChange(link.page)}
              disabled={link.label === '...'}
            >
              {link.label}
            </button>
          </li>
        ))}
    </ul>
  )
}
